/**
 * Universal Modal Component
 *
 * A flexible modal/dialog component using the KRP Design System.
 * Supports multiple sizes (sm through 5xl + full), smooth animations,
 * and customizable header/footer.
 *
 * @example
 * // Simple confirmation
 * <Modal isOpen={open} onClose={close} title="Confirm" size="sm">
 *   Are you sure?
 * </Modal>
 *
 * @example
 * // Large form with 2 columns
 * <Modal isOpen={open} onClose={close} title="Add Company" size="3xl">
 *   <div className="grid grid-cols-2 gap-4">...</div>
 * </Modal>
 */

import React, { useEffect, useRef, useCallback, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine } from 'react-icons/ri';
import { cn } from '../../utils/cn';

// ============================================================================
// TYPES
// ============================================================================

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full' | 'screen';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title - can be string or ReactNode (for icons, formatted text) */
  title: React.ReactNode;
  /** Modal content */
  children: React.ReactNode;
  /** Modal size - from sm (400px) to full (1280px) */
  size?: ModalSize;
  /** Footer content (buttons, etc.) */
  footer?: React.ReactNode;
  /** Close when clicking outside */
  closeOnClickOutside?: boolean;
  /** Close when pressing Escape */
  closeOnEsc?: boolean;
  /** Icon to show in header */
  icon?: React.ReactNode;
  /** Custom max height */
  maxHeight?: string;
  /** Hide close button */
  hideCloseButton?: boolean;
  /** Additional class for the modal container */
  className?: string;
  /** Additional class for the body */
  bodyClassName?: string;
  /** Subtitle text */
  subtitle?: string;
  /** Header variant - default or compact */
  headerVariant?: 'default' | 'compact';
  /** Whether to show divider lines */
  showDividers?: boolean;
  /** KD3 S2490: 'alertdialog' สำหรับกล่องยืนยัน/เตือนที่ต้องตัดสินใจ (screen reader อ่านเนื้อความทันที) */
  role?: 'dialog' | 'alertdialog';
  /** KD3 S2490: id ขององค์ประกอบที่อธิบายเนื้อหา — ไม่มี = screen reader อ่านแค่หัวข้อ */
  ariaDescribedBy?: string;
  /**
   * UI-012: ดันช่อง history 1 ช่องตอนเปิด ให้ปุ่ม Back ของ browser ปิดโมดัล (default true)
   * โมดัลที่ผูกกับ route (URL เป็นแหล่งความจริงเอง เช่น /billing/quotations/:id) ส่ง false → Modal ไม่แตะ history
   * และไม่ผูก popstate เลย: Back = router เปลี่ยน URL → หน้าปิดโมดัลตาม
   */
  pushHistoryOnOpen?: boolean;
}

// ============================================================================
// HISTORY (UI-012) — ตัวประสานระดับโมดูล
// ============================================================================
//
// มติเจ้าของระบบ 30 ก.ย. 2026 ข้อ 7 (F-U2-03): เดิม effect ที่ดัน history.pushState มี onClose ใน deps → หน้าที่ส่ง
// onClose เป็น arrow ใหม่ทุก render ดันช่องเพิ่มทุก re-render ขณะโมดัลเปิด (StrictMode อีกเท่าตัว) ผู้ใช้ต้องกด Back
// 3–8 ครั้งจึงออกจากหน้า และหน้าใบเสนอราคาต้องเขียนโค้ด "ถอยช่อง history เอง" อ้อมปัญหา
//
// กติกาใหม่:
//  1. เปิด 1 ครั้ง = ช่องเดียว `{ modalOpen: true, modalId }` (id เฉพาะตัวต่อ instance) · effect ขึ้นกับ isOpen เท่านั้น
//     onClose ล่าสุดอยู่ใน ref · ถ้า history.state เป็นช่องของตัวเองอยู่แล้ว (StrictMode mount→unmount→mount ·
//     Forward กลับมาช่องเดิม) ไม่ดันซ้ำ
//  2. popstate: history.state ยังเป็นช่องของตัวเอง = ช่องที่ถูกถอยเป็นของโมดัลที่ซ้อนอยู่บน (หรือ guard อื่นที่ดันทับ)
//     → ไม่ปิด · ไม่ใช่ของตัวเอง = ผู้ใช้ถอยผ่านช่องของเรา → onClose
//  3. ปิดเอง (X / Esc / คลิกนอก / บันทึกสำเร็จ / parent ถอดโมดัลทิ้ง): ถ้าช่องบนสุดยังเป็นของตัวเอง → history.back()
//     กินช่องนั้น (ไม่งั้น Back ครั้งถัดไปของผู้ใช้เป็น no-op) · ถ้าไม่ใช่ (หน้า navigate ไป URL ใหม่แล้ว / router
//     replace ทับ / โมดัลอื่นซ้อนอยู่บน) → ห้าม back เพราะจะย้อน navigation ของผู้ใช้ — ปล่อยช่องค้าง 1 ช่องดีกว่า
//     ย้อนหน้าผิด · ตัดสินจาก history.state ตอนนั้น ไม่ใช่จากธง "เคยดัน"
//  4. history.back() เป็น async: โมดัลที่เปิดต่อใน commit เดียวกัน (ปิด A → เปิดกล่องยืนยัน B) ต้องรอให้การถอยจบ
//     ก่อนค่อยดันช่องของตัวเอง ไม่งั้นการถอยกินช่องของ B แล้ว B ปิดตัวเอง (memory
//     modal-pushes-history-on-every-onclose-change) → คิว deferredPushes · popstate ที่เกิดจากการถอยของเราเอง
//     ถูกจำไว้ (selfPopEvent) ให้ handler ของทุกโมดัลข้าม ไม่ปิดโมดัลที่ยังเปิดอยู่
//  5. ปิดหลายชั้นใน commit เดียว (กล่องยืนยันบนฟอร์ม สำเร็จแล้วปิดทั้งคู่): รวมใน microtask เดียว ถอยทีละช่อง
//     และตรวจทุกครั้งที่ลงจอดว่าช่องนั้นเป็นของโมดัลที่ปิดแล้ว (staleEntryIds) จึงถอยต่อ — ไม่เดา delta ล่วงหน้า
//     เพราะ router อาจแทรกช่องระหว่างกลาง

type ModalHistoryEntry = { modalOpen: true; modalId: string };

/** id ของโมดัลเจ้าของช่อง history นี้ · null = ไม่ใช่ช่องของโมดัล (ช่องของ router / guard อื่น / เปิดตรง) */
export const modalIdOf = (state: unknown): string | null => {
  if (!state || typeof state !== 'object') return null;
  const s = state as { modalOpen?: unknown; modalId?: unknown };
  return s.modalOpen === true && typeof s.modalId === 'string' ? s.modalId : null;
};

/** ช่องของโมดัลที่ปิดแล้วแต่ยังอยู่ใน history (ปิดตอนไม่ได้อยู่บนสุด) — การถอยของโมดัลอื่นที่ลงจอดช่องนี้จะถอยต่อ */
const staleEntryIds = new Set<string>();
/** id ที่ปิดใน commit นี้ รอ microtask ตัดสินว่าต้องถอยหรือไม่ (รวมหลายชั้น + ให้ StrictMode remount ถอนออกทัน) */
const closingIds = new Set<string>();
let consumeScheduled = false;
/** การถอยที่โมดัลสั่งเองที่ยังรอ popstate · hopsLeft = เพดานการถอยต่อ */
let consumeInFlight: { hopsLeft: number; timer: ReturnType<typeof setTimeout> } | null = null;
/** popstate ล่าสุดที่เกิดจากการถอยของโมดัลเอง — handler ของทุกโมดัลข้าม event นี้ */
let selfPopEvent: PopStateEvent | null = null;
/** โมดัลที่รอดันช่องหลังการถอยจบ */
const deferredPushes: Array<{ run: () => void; cancelled: boolean }> = [];
/** id ของโมดัลที่ "ยังเปิดอยู่และเป็นเจ้าของช่อง" — ใช้ตัดสินว่า replaceState ที่ทับช่องบนสุดต้องคงเครื่องหมายไว้ไหม */
const liveEntryIds = new Set<string>();
/** popstate ไม่มา (browser บางตัว / ถอยเกินขอบ) → ถือว่าจบ ปล่อยคิวเปิดต่อ */
const CONSUME_TIMEOUT_MS = 1000;

const flushDeferredPushes = () => {
  const list = deferredPushes.splice(0);
  list.forEach((d) => {
    if (!d.cancelled) d.run();
  });
};

const settleConsume = () => {
  if (consumeInFlight) {
    clearTimeout(consumeInFlight.timer);
    consumeInFlight = null;
  }
  flushDeferredPushes();
};

/** ถอย 1 ช่องจากช่องบนสุดที่เป็นของโมดัลที่ปิดแล้ว (ผู้เรียกตรวจแล้วว่า top ∈ staleEntryIds) */
const hopBack = (topId: string, hopsLeft: number) => {
  staleEntryIds.delete(topId);
  if (consumeInFlight) clearTimeout(consumeInFlight.timer);
  consumeInFlight = { hopsLeft, timer: setTimeout(settleConsume, CONSUME_TIMEOUT_MS) };
  window.history.back();
};

const runScheduledConsume = () => {
  consumeScheduled = false;
  closingIds.forEach((id) => staleEntryIds.add(id));
  closingIds.clear();
  if (consumeInFlight) return; // การถอยรอบก่อนยังไม่จบ — ลงจอดแล้วจะตรวจ staleEntryIds ต่อเอง
  const top = modalIdOf(window.history.state);
  if (top !== null && staleEntryIds.has(top)) hopBack(top, staleEntryIds.size);
  else flushDeferredPushes();
};

const scheduleConsume = () => {
  if (consumeScheduled) return;
  consumeScheduled = true;
  queueMicrotask(runScheduledConsume);
};

/** ต้องเลื่อนการดันช่องออกไปก่อนไหม (มีการปิด/การถอยที่ยังไม่จบ) */
const mustDeferPush = () => closingIds.size > 0 || consumeScheduled || consumeInFlight !== null;

// 6. ช่องของโมดัลที่ยังเปิดถูก replaceState ทับ (E2E บน Chrome 30 ก.ย. 2026): react-router v7 อัปเดตหน้าแบบ transition
//    → effect ที่ sync ตัวกรองลง URL (setSearchParams replace) ของหน้าวิ่ง "หลัง" โมดัลดันช่องไปแล้ว ในจังหวะที่คาดเดาไม่ได้
//    เครื่องหมายของโมดัลหาย → ตอนปิดโมดัลไม่รู้ว่าเป็นเจ้าของช่อง ไม่ถอย เหลือช่องค้าง (Back 2 ครั้งจึงออกจากหน้า)
//    → ครอบ history.replaceState: ช่องบนสุดเป็นของโมดัลที่ยังเปิด และข้อมูลใหม่ไม่ใช่ช่องของโมดัล = รวมเครื่องหมายเข้าไป
//    (router อ่านเฉพาะ usr/key/idx ฟิลด์เพิ่มไม่กระทบ) · โมดัลที่ปิดแล้วไม่รวม — การถอยจะทิ้ง URL ที่หน้าเพิ่งเขียน
if (typeof window !== 'undefined' && !(window.history as { __modalReplacePatched?: boolean }).__modalReplacePatched) {
  const nativeReplace = window.history.replaceState.bind(window.history);
  (window.history as { __modalReplacePatched?: boolean }).__modalReplacePatched = true;
  window.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    const top = modalIdOf(window.history.state);
    if (top !== null && liveEntryIds.has(top) && modalIdOf(data) === null) {
      const base = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
      data = { ...base, modalOpen: true, modalId: top };
    }
    nativeReplace(data, unused, url);
  };
}

// ลงทะเบียนตอนโหลดโมดูล = ก่อน handler ของทุกโมดัล → เป็นคนแรกที่รู้ว่า popstate นี้เกิดจากการถอยของเราเอง
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (e: PopStateEvent) => {
    if (!consumeInFlight) return;
    selfPopEvent = e;
    const top = modalIdOf(window.history.state);
    if (top !== null && staleEntryIds.has(top) && consumeInFlight.hopsLeft > 0) {
      hopBack(top, consumeInFlight.hopsLeft - 1);
      return;
    }
    settleConsume();
  });
}

// ============================================================================
// SIZE CONFIGURATION
// ============================================================================

/**
 * Modal size styles with responsive considerations
 *
 * Size Strategy:
 * All sizes use CSS min() to ensure modals don't overflow on small screens
 * while maintaining a maximum width cap on larger screens.
 *
 * Use cases:
 * - sm: Confirmations, alerts, simple prompts (max 400px)
 * - md: Simple forms (1-5 fields) (max 500px)
 * - lg: Medium forms (5-10 fields) (max 600px)
 * - xl: Complex forms with sections (max 700px)
 * - 2xl: Large forms with multiple sections (max 800px)
 * - 3xl: Very large forms, 2-column layouts (max 900px)
 * - 4xl: Extra large forms, complex data entry (max 1000px)
 * - 5xl: Multi-section forms, tabbed content (max 1100px)
 * - full: Full-width content, tables, data grids (max 1280px)
 * - screen: Image/screenshot viewer — กว้างเต็มจอ (เว้นขอบ 1rem/ข้าง) ไม่มีเพดาน px
 */
// KD3 S2413 (NFR-QUOT-002): padding แนวนอนตายตัว (สูงสุด 32px/ข้าง) + กล่องซ้อนในฟอร์ม ทำให้ที่ 375px ช่องกรอกเหลือไม่กี่สิบ px
// → มือถือใช้ px-4 เท่ากันทุกขนาด ค่าเดิมเริ่มที่ sm:
const sizeConfig: Record<ModalSize, { maxWidth: string; padding: string }> = {
  // All sizes use min() to be responsive on small screens
  sm: { maxWidth: 'max-w-[min(calc(100vw-2rem),400px)]', padding: 'px-4 sm:px-5 py-4' },
  md: { maxWidth: 'max-w-[min(calc(100vw-2rem),500px)]', padding: 'px-4 sm:px-6 py-5' },
  lg: { maxWidth: 'max-w-[min(calc(100vw-2rem),600px)]', padding: 'px-4 sm:px-6 py-5' },
  xl: { maxWidth: 'max-w-[min(calc(100vw-2rem),700px)]', padding: 'px-4 sm:px-6 py-5' },
  '2xl': { maxWidth: 'max-w-[min(calc(100vw-2rem),800px)]', padding: 'px-4 sm:px-7 py-6' },
  '3xl': { maxWidth: 'max-w-[min(calc(100vw-2rem),900px)]', padding: 'px-4 sm:px-7 py-6' },
  '4xl': { maxWidth: 'max-w-[min(calc(100vw-2rem),1000px)]', padding: 'px-4 sm:px-8 py-6' },
  '5xl': { maxWidth: 'max-w-[min(calc(100vw-2rem),1100px)]', padding: 'px-4 sm:px-8 py-6' },
  full: { maxWidth: 'max-w-[min(calc(100vw-2rem),1280px)]', padding: 'px-4 sm:px-8 py-6' },
  screen: { maxWidth: 'max-w-[calc(100vw-2rem)]', padding: 'px-2 sm:px-4 py-3' },
};

// ============================================================================
// COMPONENT
// ============================================================================

// BUG-S2261-004: โมดัลซ้อนกันได้ (เช่น "เปิดรายการใหม่" บน "รายละเอียดการคืน") แต่ทุกตัวฟัง keydown
// บน document ของตัวเอง → Esc ครั้งเดียวปิดทุกชั้น และ focus trap ของโมดัลล่างดึงโฟกัสออกจาก
// โมดัลบนเมื่อกด Tab · stack นี้ให้เฉพาะโมดัลบนสุดตอบคีย์บอร์ด
const openModalStack: symbol[] = [];
const isTopModal = (token: symbol) => openModalStack[openModalStack.length - 1] === token;


/**
 * KD3 S2493 (NFR-MD-010): คืนโฟกัสให้ปุ่มที่เปิดโมดัล — แต่กล่องยืนยันลบรอลบเสร็จ+รายการโหลดใหม่ก่อนปิด
 * ปุ่มเมนูของแถวจึงหายไปพร้อมแถว → โฟกัสตกไป <body> ผู้ใช้คีย์บอร์ดต้อง Tab ใหม่จากบนสุด
 * · fallback = พื้นที่ที่ปุ่มอยู่ (role=region เช่น "ตารางข้อมูล" / เนื้อหาหลัก) ที่จับไว้ตั้งแต่ตอนเปิดโมดัล
 * · ปุ่มยังอยู่ → โฟกัสปุ่ม แล้วเฝ้า 3 วิ เผื่อแถวหายทีหลัง · ปุ่มหายแล้ว → ไป fallback ทันที
 */
export function focusFallbackFor(trigger: HTMLElement | null): HTMLElement | null {
  return (trigger?.closest('[role="region"]') as HTMLElement | null) || document.getElementById('main-content');
}

export function restoreFocusToTrigger(
  trigger: HTMLElement | null,
  fallback: HTMLElement | null = focusFallbackFor(trigger),
  watchMs = 3000,
): void {
  const focusFallback = () => {
    if (!fallback || !document.body.contains(fallback)) return;
    if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
    fallback.focus();
  };
  if (!trigger || typeof trigger.focus !== 'function' || !document.body.contains(trigger)) {
    // ถ้ามีคนอื่นย้ายโฟกัสไปที่อื่นที่ใช้ได้แล้ว (ไม่ใช่ body) ไม่แย่ง
    if (!document.activeElement || document.activeElement === document.body) focusFallback();
    return;
  }
  trigger.focus();
  const started = Date.now();
  const timer = setInterval(() => {
    // หน้า/สภาพแวดล้อมถูกถอดไปแล้ว (unmount ทั้งแอป · jsdom teardown ในเทสต์) → หยุดเฝ้า
    if (typeof document === 'undefined' || !document.body) {
      clearInterval(timer);
      return;
    }
    const lost = !document.body.contains(trigger);
    if (lost) {
      if (!document.activeElement || document.activeElement === document.body) focusFallback();
      clearInterval(timer);
    } else if (Date.now() - started > watchMs) {
      clearInterval(timer);
    }
  }, 100);
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  footer,
  closeOnClickOutside = true,
  closeOnEsc = true,
  icon,
  maxHeight,
  hideCloseButton = false,
  className,
  bodyClassName,
  subtitle,
  headerVariant = 'default',
  showDividers = true,
  role = 'dialog',
  ariaDescribedBy,
  pushHistoryOnOpen = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  // A11y: store the element that triggered the modal so focus can be returned on close
  const triggerRef = useRef<HTMLElement | null>(null);
  const focusFallbackRef = useRef<HTMLElement | null>(null);
  // BUG-S2261-002: id หัวข้อต้องไม่ซ้ำ — โมดัลซ้อน (เช่น "เปิดรายการใหม่" ทับ "รายละเอียดการคืน") เคยใช้
  // id="modal-title" เดียวกัน → aria-labelledby ของโมดัลบนชี้ไปหัวข้อของโมดัลล่าง = screen reader อ่านชื่อผิด
  const reactId = useId();
  const titleId = `modal-title-${reactId.replace(/:/g, '')}`;
  /** id ช่อง history ของโมดัลนี้ (useId คงที่ตลอดอายุ instance รวม StrictMode double render) */
  const modalId = `modal-${reactId}`;
  const stackTokenRef = useRef<symbol>(Symbol('modal'));
  // onClose ล่าสุด — handler ของ popstate/Esc อ่านจาก ref เพื่อไม่ต้องเอา onClose ใส่ deps ของ effect
  // (หน้าเกือบทั้งหมดส่ง onClose เป็น arrow ใหม่ทุก render)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Handle animation states + A11y focus management
  useEffect(() => {
    if (isOpen) {
      // Capture the trigger element before the modal takes focus
      triggerRef.current = document.activeElement as HTMLElement;
      focusFallbackRef.current = focusFallbackFor(triggerRef.current);
      setShouldRender(true);
      // Small delay to ensure DOM is ready for animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      // BUG-S57-001 FIX: restore focus to the trigger SYNCHRONOUSLY on close,
      // NOT inside the 200ms exit-animation timer. Many parents unmount the modal
      // immediately on close (e.g. clearing the selected entity → child returns null),
      // which fires this effect's cleanup and clearTimeout()s the restore — so focus
      // was never returned and landed on <body>. Restoring here runs before any unmount.
      // Guard on document.contains so we never focus() a detached node (no-op → body).
      restoreFocusToTrigger(triggerRef.current, focusFallbackRef.current);
      triggerRef.current = null;
      // Keep the exit-animation delay only for unmounting the rendered modal.
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // BUG-S57-001 FIX: restore focus on UNMOUNT too. Many parents close a modal by
  // unmounting it (e.g. clearing the selected entity → child returns null) rather than
  // re-rendering with isOpen=false, so the isOpen=false branch above never runs. This
  // unmount-only cleanup guarantees focus returns to the trigger in that path. The two
  // paths are mutually exclusive (a modal either re-renders isOpen=false while mounted,
  // or unmounts), so there is no double-focus.
  useEffect(() => {
    return () => {
      restoreFocusToTrigger(triggerRef.current, focusFallbackRef.current);
    };
  }, []);

  // Handle ESC key press + UI-011: focus trap (Tab/Shift+Tab stays inside modal)
  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (!isTopModal(stackTokenRef.current)) return;
      if (closeOnEsc && event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      // UI-011 + BUG-S1116-001 FIX: Focus trap — keep Tab/Shift+Tab inside the modal
      if (event.key === 'Tab' && modalRef.current) {
        const focusableSelectors =
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
        const focusableEls = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
        ).filter((el) => !el.closest('[aria-hidden="true"]'));

        if (focusableEls.length === 0) return;

        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];
        const activeInsideModal = modalRef.current.contains(document.activeElement);

        // BUG-S1116-001 FIX: if focus escaped outside modal (e.g., initial body focus), pull it back in
        if (!activeInsideModal) {
          event.preventDefault();
          (event.shiftKey ? lastEl : firstEl).focus();
          return;
        }

        if (event.shiftKey) {
          // Shift+Tab: if focus is on first element, wrap to last
          if (document.activeElement === firstEl) {
            event.preventDefault();
            lastEl.focus();
          }
        } else {
          // Tab: if focus is on last element, wrap to first
          if (document.activeElement === lastEl) {
            event.preventDefault();
            firstEl.focus();
          }
        }
      }
    },
    [closeOnEsc]
  );

  // ลำดับซ้อนของโมดัล — ผูกกับ isOpen อย่างเดียว (effect ด้านล่างวิ่งซ้ำทุกครั้งที่ onClose เปลี่ยน
  // ถ้า push/pop ในนั้น โมดัลล่างที่ re-render จะถูกดันขึ้นเป็น "บนสุด" ผิดลำดับ) · ประกาศก่อน effect
  // หลักเพื่อให้ cleanup วิ่งก่อน → effect หลักเห็น stack ที่ถอดตัวเองออกแล้วตอนตัดสินเรื่อง overflow
  useEffect(() => {
    if (!isOpen) return;
    const token = stackTokenRef.current;
    openModalStack.push(token);
    return () => {
      const idx = openModalStack.indexOf(token);
      if (idx >= 0) openModalStack.splice(idx, 1);
    };
  }, [isOpen]);

  // UI-012: ช่อง history ของโมดัล — ขึ้นกับ isOpen/pushHistoryOnOpen เท่านั้น (กติกาอยู่ที่หัวข้อ HISTORY ด้านบน)
  useEffect(() => {
    if (!isOpen || !pushHistoryOnOpen) return;
    const id = modalId;
    // StrictMode: cleanup ของรอบจำลองเพิ่งใส่ id นี้ในคิวปิด → ถอนออก ช่องยังอยู่บนสุด ไม่ต้องดันซ้ำ
    closingIds.delete(id);
    staleEntryIds.delete(id);

    let ownsEntry = false;
    let deferred: { run: () => void; cancelled: boolean } | null = null;
    const handlePopState = (e: PopStateEvent) => {
      // การถอยที่โมดัล (ตัวใดตัวหนึ่ง) สั่งเองตอนปิด ไม่ใช่ผู้ใช้กด Back
      if (e === selfPopEvent) return;
      // ยังอยู่บนช่องของตัวเอง = ช่องที่ถูกถอยเป็นของโมดัลที่ซ้อนอยู่บน / guard อื่น → ไม่ปิด
      if (modalIdOf(window.history.state) === id) return;
      onCloseRef.current();
    };
    const takeEntry = () => {
      if (modalIdOf(window.history.state) !== id) {
        const entry: ModalHistoryEntry = { modalOpen: true, modalId: id };
        window.history.pushState(entry, '');
      }
      ownsEntry = true;
      liveEntryIds.add(id);
      window.addEventListener('popstate', handlePopState);
    };

    if (modalIdOf(window.history.state) === id) takeEntry();
    else {
      // ดันช่องใน microtask = หลัง effect ทั้งหมดของ commit นี้ (React รัน effect ลูกก่อนแม่) — E2E 30 ก.ย. 2026:
      // เปิดโมดัลต่ออายุจากโมดัลของ route ในหน้าที่ sync ตัวกรองลง URL ใน effect ของแม่ → router.replace ทับช่องที่
      // โมดัลเพิ่งดัน โมดัลเลยไม่ใช่เจ้าของช่องบนสุดตอนปิด ไม่ถอย เหลือช่องค้าง 1 ช่อง (Back 2 ครั้งจึงออกจากหน้า)
      // · ถ้ามีการปิด/การถอยที่ยังไม่จบ (ปิด A → เปิด B ใน commit เดียว) รอให้การถอยจบก่อนค่อยดัน
      deferred = { run: takeEntry, cancelled: false };
      const d = deferred;
      queueMicrotask(() => {
        if (d.cancelled) return;
        if (mustDeferPush()) deferredPushes.push(d);
        else d.run();
      });
    }

    return () => {
      if (deferred) deferred.cancelled = true;
      liveEntryIds.delete(id);
      window.removeEventListener('popstate', handlePopState);
      // ปิด (isOpen→false) หรือ parent ถอดโมดัลทั้งที่เปิดอยู่ — จะถอยหรือไม่ตัดสินใน microtask จาก history.state
      // ตอนนั้น: ช่องบนสุดเป็นของเรา → back() · ไม่ใช่ (หน้าเปลี่ยน route ไปแล้ว) → ห้าม back ปล่อยช่องค้าง
      if (!ownsEntry) return;
      closingIds.add(id);
      scheduleConsume();
    };
  }, [isOpen, pushHistoryOnOpen, modalId]);

  // Track whether we already focused on open (prevent re-focus on every re-render)
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      // BUG-S2231-001: ปุ่มลอย (แจ้งปัญหา/คู่มือ) อยู่เหนือโมดัลโดยตั้งใจ (z-popover) แต่บนจอเล็ก
      // โมดัลเต็มจอ → ปุ่มลอยทับปุ่ม "บันทึก" ของโมดัล · นับจำนวนโมดัลที่เปิด (ซ้อนกันได้) ให้ CSS จัดตำแหน่งใหม่
      const openCount = Number(document.body.dataset.modalOpen || 0) + 1;
      document.body.dataset.modalOpen = String(openCount);

      // Focus the first focusable element ONLY on initial open (not on every re-render)
      // BUG-S1116-001 FIX: use double rAF + setTimeout fallback to handle React render races
      if (!hasFocusedRef.current) {
        hasFocusedRef.current = true;
        const focusableSelectors =
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
        const tryFocusFirst = () => {
          if (modalRef.current && !modalRef.current.contains(document.activeElement)) {
            const firstEl = modalRef.current.querySelector<HTMLElement>(focusableSelectors);
            firstEl?.focus();
          }
        };
        // First try: after current paint
        requestAnimationFrame(() => {
          requestAnimationFrame(tryFocusFirst);
        });
        // Fallback: in case React's render lifecycle delayed mounting children
        setTimeout(tryFocusFirst, 100);
      }

      return () => {
        document.removeEventListener('keydown', handleEscKey);
        // ยังมีโมดัลอื่นเปิดอยู่ = ห้ามปลดล็อก scroll ของ body
        if (openModalStack.length === 0) document.body.style.overflow = 'auto';
        const remaining = Number(document.body.dataset.modalOpen || 1) - 1;
        if (remaining > 0) document.body.dataset.modalOpen = String(remaining);
        else delete document.body.dataset.modalOpen;
      };
    } else {
      if (openModalStack.length === 0) document.body.style.overflow = 'auto';
      hasFocusedRef.current = false; // Reset when modal closes
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      if (openModalStack.length === 0) document.body.style.overflow = 'auto';
    };
  }, [isOpen, handleEscKey]);

  // Handle clicks outside the modal
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnClickOutside && overlayRef.current === event.target) {
      onClose();
    }
  };

  // Get size config
  const { maxWidth, padding } = sizeConfig[size];

  // Don't render anything if modal is not visible
  if (!shouldRender) return null;

  // Use createPortal to render modal at document.body level
  // This ensures nested modals render properly on top of parent modals
  const modalContent = (
    <div
      className="fixed inset-0 z-modal overflow-hidden flex items-center justify-center p-4 sm:p-6"
      aria-labelledby={titleId}
      aria-describedby={ariaDescribedBy}
      role={role}
      aria-modal="true"
    >
      {/* Overlay with backdrop blur */}
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        className={cn(
          'fixed inset-0',
          'bg-secondary-900/60 backdrop-blur-sm',
          'transition-all duration-200 ease-out',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
      />

      {/* Modal Container */}
      <div
        ref={modalRef}
        className={cn(
          // Base styles
          'relative z-[1401]',
          'bg-white rounded-2xl',
          'shadow-2xl shadow-secondary-900/20',
          'w-full flex flex-col overflow-hidden',
          // Animation
          'transition-all duration-200 ease-out',
          isAnimating
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-[0.98] translate-y-2',
          // Size - each size already includes viewport constraint via min()
          maxWidth,
          // Custom class
          className
        )}
        style={{ maxHeight: maxHeight || 'calc(100vh - 3rem)' }}
      >
        {/* Header */}
        <div
          className={cn(
            headerVariant === 'compact' ? 'px-5 py-3' : 'px-6 py-4',
            showDividers && 'border-b border-secondary-200',
            'flex items-center justify-between flex-shrink-0',
            'bg-secondary-50 border-b border-secondary-100'
          )}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {icon && (
              <div
                className={cn(
                  'flex-shrink-0 rounded-xl',
                  'bg-gradient-to-br from-primary-50 to-primary-100',
                  'text-primary-600 flex items-center justify-center',
                  'shadow-sm shadow-primary-100',
                  headerVariant === 'compact' ? 'w-9 h-9' : 'w-11 h-11'
                )}
              >
                {icon}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3
                className={cn(
                  'font-semibold text-secondary-900 truncate',
                  headerVariant === 'compact' ? 'text-base' : 'text-lg'
                )}
                id={titleId}
              >
                {title}
              </h3>
              {subtitle && (
                <p className="text-sm text-secondary-500 mt-0.5 truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'flex-shrink-0 ml-4',
                'p-2 -mr-2 rounded-xl',
                'text-secondary-400 hover:text-secondary-600',
                'hover:bg-secondary-100',
                'transition-all duration-150',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-1',
                'active:scale-95'
              )}
              aria-label="ปิด"
            >
              <RiCloseLine className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div
          tabIndex={0}
          role="region"
          aria-labelledby={titleId}
          className={cn(
            padding,
            'flex-grow overflow-y-auto overflow-x-hidden',
            // Custom scrollbar
            'scrollbar-thin scrollbar-thumb-secondary-300 scrollbar-track-transparent',
            'hover:scrollbar-thumb-secondary-400',
            bodyClassName
          )}
          style={{ minHeight: '100px' }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className={cn(
              'px-6 py-4',
              showDividers && 'border-t border-secondary-200',
              'flex justify-end items-center gap-3 flex-shrink-0',
              'bg-gradient-to-t from-secondary-50/80 to-white'
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render modal using Portal at document.body level
  return createPortal(modalContent, document.body);
};

export default Modal;
